import React, { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Home, Eye, EyeOff, Shield, RefreshCw, ArrowLeft, KeyRound, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useT, LanguageSwitcher } from '@/i18n';

const LoginPage = () => {
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // 2FA state
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp' | 'forgot' | 'reset-otp' | 'new-password'
  const [tempToken, setTempToken] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const otpRefs = useRef([]);

  // Password recovery state
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetCode, setResetCode] = useState(['', '', '', '', '', '']);
  const [resetCodeLoading, setResetCodeLoading] = useState(false);
  const resetCodeRefs = useRef([]);
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  const { login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/dashboard';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);

    if (result.success) {
      // 2FA not required (shouldn't happen in normal flow, but handle gracefully)
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } else if (result.requires_2fa) {
      // Switch to OTP step
      setTempToken(result.temp_token);
      setStep('otp');
      toast.info('Check your email for a 6-digit verification code');
    } else {
      toast.error(result.error);
    }

    setLoading(false);
  };

  const handleOtpChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value.slice(-1); // only last char if paste
    setOtp(newOtp);
    // Auto-advance
    if (value && index < 5) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setOtp(pasted.split(''));
      otpRefs.current[5]?.focus();
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length !== 6) {
      toast.error('Please enter all 6 digits');
      return;
    }

    setOtpLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-otp`, {
        temp_token: tempToken,
        otp_code: code
      });

      const { access_token, user: userData } = response.data;
      loginWithToken(access_token, userData);
      toast.success('Welcome back!');
      navigate(from, { replace: true });
    } catch (error) {
      const msg = error.response?.data?.detail || 'Invalid or expired code';
      toast.error(msg);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } finally {
      setOtpLoading(false);
    }
  };

  const handleResend = async () => {
    setResendLoading(true);
    try {
      await axios.post(`${API}/auth/resend-otp`, { temp_token: tempToken });
      toast.success('A new code has been sent');
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
    } catch (error) {
      toast.error('Could not resend code, please try logging in again');
    } finally {
      setResendLoading(false);
    }
  };

  // ── Password recovery handlers ────────────────────────────────────────────

  const handleForgotSubmit = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('Reset code sent — check your email');
      setResetCode(['', '', '', '', '', '']);
      setStep('reset-otp');
    } catch {
      toast.error('Something went wrong, please try again');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleResetCodeChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const next = [...resetCode];
    next[index] = value.slice(-1);
    setResetCode(next);
    if (value && index < 5) resetCodeRefs.current[index + 1]?.focus();
  };

  const handleResetCodeKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !resetCode[index] && index > 0)
      resetCodeRefs.current[index - 1]?.focus();
  };

  const handleResetCodePaste = (e) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 6) {
      setResetCode(pasted.split(''));
      resetCodeRefs.current[5]?.focus();
    }
  };

  const handleVerifyResetCode = async (e) => {
    e.preventDefault();
    const code = resetCode.join('');
    if (code.length !== 6) { toast.error('Please enter all 6 digits'); return; }
    // Just advance — actual verification happens on password submit to avoid
    // burning the token if the user mis-types their password.
    setStep('new-password');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== newPasswordConfirm) { toast.error('Passwords do not match'); return; }
    if (newPassword.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    setResetLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        email: forgotEmail,
        code: resetCode.join(''),
        new_password: newPassword,
      });
      setResetDone(true);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to reset password';
      toast.error(msg);
      // If code was invalid/expired, send them back to re-enter it
      if (error.response?.status === 400) setStep('reset-otp');
    } finally {
      setResetLoading(false);
    }
  };

  const backToLogin = () => {
    setStep('credentials');
    setForgotEmail('');
    setResetCode(['', '', '', '', '', '']);
    setNewPassword('');
    setNewPasswordConfirm('');
    setResetDone(false);
  };


  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        background: 'linear-gradient(135deg, #0c2431 0%, #194a50 50%, #10555e 100%)'
      }}
      data-testid="login-page"
    >
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-[#d8a28c]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-[#194a50]/30 rounded-full blur-3xl" />
      </div>

      <Card className="w-full max-w-md relative z-10 shadow-2xl border-0" data-testid="login-card">
        {step === 'forgot' ? (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#194a50] flex items-center justify-center">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#0c2431] font-['Outfit']">
                Recover Password
              </CardTitle>
              <CardDescription className="text-[#194a50]/70">
                Enter your email and we'll send you a 6-digit reset code
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleForgotSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email" className="text-[#0c2431]">Email address</Label>
                  <Input
                    id="forgot-email"
                    type="email"
                    placeholder="you@example.com"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="border-[#194a50]/20 focus:border-[#194a50]"
                    data-testid="forgot-email-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#194a50] hover:bg-[#10555e] text-white rounded-full py-6"
                  disabled={forgotLoading}
                  data-testid="forgot-submit-btn"
                >
                  {forgotLoading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
              <button
                onClick={backToLogin}
                className="w-full flex items-center justify-center gap-2 text-sm text-[#194a50]/60 hover:text-[#194a50] transition-colors py-1"
              >
                <ArrowLeft className="w-4 h-4" /> Back to login
              </button>
            </CardContent>
          </>
        ) : step === 'reset-otp' ? (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#194a50] flex items-center justify-center">
                <KeyRound className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#0c2431] font-['Outfit']">
                Enter Reset Code
              </CardTitle>
              <CardDescription className="text-[#194a50]/70">
                We sent a 6-digit code to<br />
                <span className="font-medium text-[#194a50]">{forgotEmail}</span>
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <form onSubmit={handleVerifyResetCode} className="space-y-6">
                <div className="flex justify-center gap-3" onPaste={handleResetCodePaste}>
                  {resetCode.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (resetCodeRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleResetCodeChange(i, e.target.value)}
                      onKeyDown={(e) => handleResetCodeKeyDown(i, e)}
                      className="w-12 h-14 text-center text-xl font-bold border-2 border-[#194a50]/20 rounded-xl focus:border-[#194a50] focus:outline-none focus:ring-2 focus:ring-[#194a50]/20 transition-all"
                      data-testid={`reset-code-input-${i}`}
                    />
                  ))}
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#194a50] hover:bg-[#10555e] text-white rounded-full py-6"
                  disabled={resetCodeLoading || resetCode.join('').length !== 6}
                >
                  {resetCodeLoading ? 'Verifying...' : 'Continue'}
                </Button>
              </form>
              <div className="space-y-2 pt-2 border-t border-[#194a50]/10">
                <button
                  onClick={handleForgotSubmit.bind(null, { preventDefault: () => {} })}
                  className="w-full flex items-center justify-center gap-2 text-sm text-[#194a50] hover:text-[#0c2431] transition-colors py-2"
                >
                  <RefreshCw className="w-4 h-4" /> Resend code
                </button>
                <button
                  onClick={backToLogin}
                  className="w-full flex items-center justify-center gap-2 text-sm text-[#194a50]/60 hover:text-[#194a50] transition-colors py-1"
                >
                  <ArrowLeft className="w-4 h-4" /> Back to login
                </button>
              </div>
            </CardContent>
          </>
        ) : step === 'new-password' ? (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#194a50] flex items-center justify-center">
                {resetDone ? <CheckCircle2 className="w-8 h-8 text-white" /> : <KeyRound className="w-8 h-8 text-white" />}
              </div>
              <CardTitle className="text-2xl font-bold text-[#0c2431] font-['Outfit']">
                {resetDone ? 'Password Updated!' : 'Set New Password'}
              </CardTitle>
              <CardDescription className="text-[#194a50]/70">
                {resetDone
                  ? 'Your password has been changed. You can now log in.'
                  : 'Choose a strong password with at least 8 characters'}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {resetDone ? (
                <Button
                  onClick={backToLogin}
                  className="w-full bg-[#194a50] hover:bg-[#10555e] text-white rounded-full py-6"
                >
                  Back to Login
                </Button>
              ) : (
                <form onSubmit={handleResetPassword} className="space-y-4">
                  <div className="space-y-2">
                    <Label className="text-[#0c2431]">New Password</Label>
                    <div className="relative">
                      <Input
                        type={showNewPassword ? 'text' : 'password'}
                        placeholder="••••••••"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        minLength={8}
                        className="border-[#194a50]/20 focus:border-[#194a50] pr-10"
                        data-testid="new-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#194a50]/50 hover:text-[#194a50]"
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-[#0c2431]">Confirm Password</Label>
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={newPasswordConfirm}
                      onChange={(e) => setNewPasswordConfirm(e.target.value)}
                      required
                      minLength={8}
                      className="border-[#194a50]/20 focus:border-[#194a50]"
                      data-testid="confirm-password-input"
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-[#194a50] hover:bg-[#10555e] text-white rounded-full py-6"
                    disabled={resetLoading}
                    data-testid="reset-password-btn"
                  >
                    {resetLoading ? 'Saving...' : 'Save New Password'}
                  </Button>
                  <button
                    type="button"
                    onClick={() => setStep('reset-otp')}
                    className="w-full flex items-center justify-center gap-2 text-sm text-[#194a50]/60 hover:text-[#194a50] transition-colors py-1"
                  >
                    <ArrowLeft className="w-4 h-4" /> Back
                  </button>
                </form>
              )}
            </CardContent>
          </>
        ) : step === 'credentials' ? (
          <>
            <CardHeader className="text-center pb-2">
              <div className="mb-5">
                <img
                  src="/logo-dark.svg"
                  alt="OceanHome"
                  className="h-12 w-auto mx-auto"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    e.currentTarget.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="w-16 h-16 mx-auto rounded-2xl bg-[#194a50] items-center justify-center hidden">
                  <Home className="w-8 h-8 text-white" />
                </div>
              </div>
              <CardTitle className="text-2xl font-bold text-[#0c2431] font-['Outfit']">
                {t('auth.login_title')}
              </CardTitle>
              <CardDescription className="text-[#194a50]/70">
                {t('auth.login_subtitle')}
              </CardDescription>
              {/* Language switcher — sits under the subtitle on the login card. */}
              <div className="flex justify-center pt-2">
                <LanguageSwitcher className="text-xs border border-[#194a50]/20 rounded-lg px-2 py-1 bg-white" />
              </div>
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-[#0c2431]">{t('auth.email')}</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('auth.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="border-[#194a50]/20 focus:border-[#194a50] focus:ring-[#194a50]"
                    data-testid="email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-[#0c2431]">{t('auth.password')}</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="border-[#194a50]/20 focus:border-[#194a50] focus:ring-[#194a50] pr-10"
                      data-testid="password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-[#194a50]/50 hover:text-[#194a50]"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#194a50] hover:bg-[#10555e] text-white rounded-full py-6"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? t('auth.signing_in') : t('auth.sign_in')}
                </Button>

                <div className="text-center">
                  <button
                    type="button"
                    onClick={() => { setForgotEmail(email); setStep('forgot'); }}
                    className="text-sm text-[#194a50]/60 hover:text-[#194a50] transition-colors"
                    data-testid="forgot-password-link"
                  >
                    {t('auth.forgot_password')}
                  </button>
                </div>
              </form>


            </CardContent>
          </>
        ) : (
          <>
            <CardHeader className="text-center pb-2">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-[#194a50] flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
              <CardTitle className="text-2xl font-bold text-[#0c2431] font-['Outfit']">
                Two-Step Verification
              </CardTitle>
              <CardDescription className="text-[#194a50]/70">
                Enter the 6-digit code sent to<br />
                <span className="font-medium text-[#194a50]">{email}</span>
              </CardDescription>
            </CardHeader>

            <CardContent className="space-y-6">
              <form onSubmit={handleVerifyOtp} className="space-y-6">
                {/* OTP boxes */}
                <div className="flex justify-center gap-3" onPaste={handleOtpPaste}>
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      className="w-12 h-14 text-center text-xl font-bold border-2 border-[#194a50]/20 rounded-xl focus:border-[#194a50] focus:outline-none focus:ring-2 focus:ring-[#194a50]/20 transition-all"
                      data-testid={`otp-input-${i}`}
                    />
                  ))}
                </div>

                <Button
                  type="submit"
                  className="w-full bg-[#194a50] hover:bg-[#10555e] text-white rounded-full py-6"
                  disabled={otpLoading || otp.join('').length !== 6}
                  data-testid="verify-otp-btn"
                >
                  {otpLoading ? 'Verifying...' : 'Verify Code'}
                </Button>
              </form>

              <div className="space-y-3 pt-2 border-t border-[#194a50]/10">
                <button
                  onClick={handleResend}
                  disabled={resendLoading}
                  className="w-full flex items-center justify-center gap-2 text-sm text-[#194a50] hover:text-[#0c2431] transition-colors py-2"
                  data-testid="resend-otp-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${resendLoading ? 'animate-spin' : ''}`} />
                  {resendLoading ? 'Sending...' : 'Resend code'}
                </button>
                <button
                  onClick={() => { setStep('credentials'); setOtp(['', '', '', '', '', '']); setTempToken(''); }}
                  className="w-full flex items-center justify-center gap-2 text-sm text-[#194a50]/60 hover:text-[#194a50] transition-colors py-1"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to login
                </button>
              </div>

              <p className="text-xs text-[#194a50]/50 text-center">
                Didn't receive a code? Check your spam folder or click Resend.
              </p>
            </CardContent>
          </>
        )}
      </Card>
    </div>
  );
};

export default LoginPage;
